// Helpers for converting Money entities between currencies.
//
// Rates come from the marketplace's own /api/exchange-rate endpoint,
// which is backed by ECB rates (see server/api-util/fx.js).

import Decimal from 'decimal.js';

import appSettings from '../config/settings';
import { types as sdkTypes } from './sdkLoader';
import { unitDivisor } from './currency';

const { Money } = sdkTypes;

/**
 * Get the localized symbol of the given currency. E.g. "$" for USD.
 *
 * Note: the 'narrowSymbol' display is used on purpose. The default 'symbol'
 * display only resolves a real symbol for the locale's own currency and falls
 * back to the ISO code for the rest (e.g. "USD" instead of "$" in en-AU).
 * Narrow symbols are ambiguous between dollar currencies, so always show the
 * currency code next to the symbol.
 *
 * Falls back to the currency code if no symbol can be resolved.
 *
 * @param {Object} intl - React Intl instance
 * @param {string} currency - ISO currency code
 *
 * @return {String} currency symbol
 */
export const getCurrencySymbol = (intl, currency) => {
  const formatSymbol = currencyDisplay => {
    try {
      const parts = intl.formatNumberToParts(0, {
        ...appSettings.getCurrencyFormatting(currency),
        currencyDisplay,
      });
      return parts.find(part => part.type === 'currency')?.value;
    } catch (e) {
      // Older browsers throw on 'narrowSymbol'.
      return null;
    }
  };

  return formatSymbol('narrowSymbol') || formatSymbol('symbol') || currency;
};

/**
 * Get the localized name of the given currency. E.g. "US Dollar" for USD.
 * Falls back to the currency code if the name can't be resolved.
 *
 * @param {Object} intl - React Intl instance
 * @param {string} currency - ISO currency code
 *
 * @return {String} currency name
 */
export const getCurrencyName = (intl, currency) => {
  try {
    return new Intl.DisplayNames([intl.locale], { type: 'currency' }).of(currency) || currency;
  } catch (e) {
    // Intl.DisplayNames is not available in every browser.
    return currency;
  }
};

/**
 * Convert an amount in minor units from one currency to another.
 * Currencies don't necessarily share the same subunit divisor (e.g. JPY has none),
 * so the amount is converted through major units.
 *
 * @param {number} amount - amount in minor units of fromCurrency
 * @param {string} fromCurrency - ISO currency code
 * @param {string} toCurrency - ISO currency code
 * @param {number} rate - fromCurrency -> toCurrency rate
 *
 * @return {number} amount in minor units of toCurrency
 */
export const convertSubUnitAmount = (amount, fromCurrency, toCurrency, rate) => {
  if (fromCurrency === toCurrency) {
    return Math.round(Number(amount));
  }

  const fromDivisor = new Decimal(unitDivisor(fromCurrency));
  const toDivisor = new Decimal(unitDivisor(toCurrency));

  return new Decimal(amount)
    .dividedBy(fromDivisor)
    .times(new Decimal(rate))
    .times(toDivisor)
    .toDecimalPlaces(0)
    .toNumber();
};

/**
 * Convert a Money entity to the given currency.
 *
 * @param {Money} money
 * @param {string} toCurrency - ISO currency code
 * @param {number} rate - money.currency -> toCurrency rate
 *
 * @return {Money} converted Money (or null if conversion is not possible)
 */
export const convertMoney = (money, toCurrency, rate) => {
  if (!(money instanceof Money) || !toCurrency) {
    return null;
  }
  if (money.currency === toCurrency) {
    return money;
  }
  if (!rate) {
    return null;
  }

  return new Money(
    convertSubUnitAmount(money.amount, money.currency, toCurrency, rate),
    toCurrency
  );
};

/**
 * Resolve the from -> to rate out of a rate table quoted against a single base currency.
 *
 * @param {Object} rates - e.g. { AUD: 1, USD: 0.65 } when base is AUD
 * @param {string} fromCurrency - ISO currency code
 * @param {string} toCurrency - ISO currency code
 *
 * @return {number|null} conversion rate, or null if either currency is missing
 */
export const getRate = (rates, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) {
    return 1;
  }
  const fromRate = rates?.[fromCurrency];
  const toRate = rates?.[toCurrency];

  return fromRate && toRate ? toRate / fromRate : null;
};
